// apps/client/src/game/systems/fishing.ts (M1-T15) — the local cast → nibble → bite → reel loop, and the ExtSystem (slot 'interaction')
// that ticks it, animates the fishing layer, refreshes your fish shadows once per town hour and drives tree shake wobbles.
import { getItem, tuning, type FishEntry } from '@whiskerton/content';
import type { FishShadowView, Vec2 } from '@whiskerton/shared';
import { buildFish } from '../../assets/builders/fish';
import type { Input } from '../../input/input';
import type { ActResult } from '../../net/actions';
import { objectsOf } from '../../net/handlers/nature';
import { useGame } from '../../state/store';
import { fishingLayerOf, ROD_LEAN } from '../../world/fishing';
import { setTreeClock, tickTreeShakes } from '../../world/objects/tree';
import type { CoreCommands } from '../commands/core';
import type { ExtSystem, Game } from '../game';

export type FishPhase = 'idle' | 'casting' | 'waiting' | 'bite' | 'reeling';
export interface FishingDeps {
  now(): number; act: CoreCommands['act'];
  view: { cast(at: Vec2): void; nibble(): void; bite(): void; clear(): void };
  haptic(ms: number): void; onPhase(p: FishPhase): void; onResult(r: ActResult<'fish.reel'>, shadow: FishShadowView): void;
}
/** How long after the bite the loop stops waiting for A and reels a miss itself (the server's window plus a little slack). */
const AUTO_MISS_SLACK_MS = 400;
const NIBBLE_BUZZ_MS = 15, BITE_BUZZ_MS = 60;

export class FishingLoop {
  private _phase: FishPhase = 'idle';
  get phase(): FishPhase { return this._phase; }
  private cur: { id: string; shadow: FishShadowView; biteAt: number; nibbleAt: number[]; biteShownAt: number | null } | null = null;
  private castSeq = 0;
  private readonly deps: FishingDeps;
  constructor(deps: FishingDeps) { this.deps = deps; }
  private set(p: FishPhase) { this._phase = p; this.deps.onPhase(p); }
  /** fish.cast; nibbles are spread evenly between 35 % and 85 % of biteInMs (one nibble sits at 60 %). */
  async cast(shadow: FishShadowView): Promise<void> {
    if (this.phase !== 'idle') return;
    const token = ++this.castSeq;
    this.set('casting');
    const at = { x: shadow.x, y: shadow.y };
    this.deps.view.cast(at);
    const res = await this.deps.act('fish.cast', { shadow: shadow.id, at });
    if (token !== this.castSeq) {                                          // B while the cast was in flight: take the line back in
      if (res.ok) void this.deps.act('fish.cancel', { cast: res.r.cast });
      return;
    }
    if (!res.ok) { this.deps.view.clear(); this.set('idle'); return; }
    const t0 = this.deps.now(), { biteInMs, nibbles, cast } = res.r;
    const nibbleAt = Array.from({ length: nibbles }, (_, i) => t0 + biteInMs * (nibbles === 1 ? 0.6 : 0.35 + (0.5 * i) / (nibbles - 1)));
    this.cur = { id: cast, shadow, biteAt: t0 + biteInMs, nibbleAt, biteShownAt: null };
    this.set('waiting');
  }
  /** Fires due nibbles, the bite, and the auto-miss once the window (plus slack) has passed. */
  tick(): void {
    const c = this.cur, now = this.deps.now();
    if (!c) return;
    if (this.phase === 'waiting') {
      while (c.nibbleAt.length && c.nibbleAt[0]! <= now) { c.nibbleAt.shift(); this.deps.view.nibble(); this.deps.haptic(NIBBLE_BUZZ_MS); }
      if (now >= c.biteAt) { c.biteShownAt = now; this.deps.view.bite(); this.deps.haptic(BITE_BUZZ_MS); this.set('bite'); }
    } else if (this.phase === 'bite' && c.biteShownAt !== null && now - c.biteShownAt > tuning.fishing.windowMs + AUTO_MISS_SLACK_MS) {
      void this.reel(tuning.fishing.windowMs + 1);
    }
  }
  /** A: before the bite → reactMs 0 (early); after it → the measured reaction time. */
  async press(): Promise<void> {
    const c = this.cur;
    if (!c || (this.phase !== 'waiting' && this.phase !== 'bite')) return;
    await this.reel(this.phase === 'waiting' || c.biteShownAt === null ? 0 : Math.round(this.deps.now() - c.biteShownAt));
  }
  /** B: fish.cancel (also while the cast is still in flight). */
  async cancel(): Promise<void> {
    if (this.phase === 'casting') { this.castSeq++; this.deps.view.clear(); this.set('idle'); return; }
    const c = this.cur;
    if (!c) return;
    this.cur = null; this.deps.view.clear(); this.set('idle');
    await this.deps.act('fish.cancel', { cast: c.id });
  }
  private async reel(reactMs: number): Promise<void> {
    const c = this.cur!;
    this.cur = null; this.set('reeling');
    const res = await this.deps.act('fish.reel', { cast: c.id, reactMs });
    this.deps.view.clear();
    this.deps.onResult(res, c.shadow);
    this.set('idle');
  }
}

const loops = new WeakMap<object, FishingLoop>();
/** Internal (beyond the M1-T15 contract): the game's loop, for interactions/fish.ts and the HUD. */
export const loopOf = (game: Game | object): FishingLoop | null => loops.get(game) ?? null;
/** Moving the stick reels in the line (fish.cancel); so does ending up further than this from where you cast (tap-to-walk, a push).
 *  Loose on purpose: a small server position correction while you stand still must not cancel a cast. */
const WALK_AWAY = 1;
/** Stick deflection that counts as walking off (below it, a resting thumb). */
const STICK_DEADZONE = 0.2;

function vibrate(ms: number): void {
  if (!useGame.getState().settings.haptics || typeof navigator === 'undefined' || !('vibrate' in navigator)) return;
  try { navigator.vibrate(ms); } catch { /* not allowed before a gesture on some browsers */ }
}

export const system: ExtSystem = {
  slot: 'interaction',
  create(game) {
    const me = () => useGame.getState().you?.resident;
    let castFrom: Vec2 | null = null;
    const loop = new FishingLoop({
      now: () => performance.now(),
      act: (t, r, o) => game.cmd.act(t, r, o),
      view: {
        cast: (at) => {
          const you = game.you();
          castFrom = you ? { ...you.pos } : null;
          you?.avatar.play('cast'); game.sound.sfx('swish');
          you?.avatar.leanHeld(ROD_LEAN);                                // the rod held out over the water while the line is out
          const r = me(); if (r) fishingLayerOf(game)?.showBobber(r, at, (out) => game.you()?.avatar.heldTip(out) ?? false);
        },
        nibble: () => { const r = me(); if (r) fishingLayerOf(game)?.dip(r); game.sound.sfx('nibble'); },
        bite: () => { const r = me(); if (r) fishingLayerOf(game)?.plunge(r); game.sound.sfx('bite'); },
        clear: () => { castFrom = null; game.you()?.avatar.leanHeld(0); const r = me(); if (r) fishingLayerOf(game)?.clearBobber(r); },
      },
      haptic: vibrate,
      onPhase: (p) => useGame.getState().fishingSetPhase(p),
      onResult: (res, shadow) => {
        game.you()?.avatar.leanHeld(0);
        if (!res.ok) return;
        const s = useGame.getState();
        if (res.r.result === 'caught' && res.r.item) {
          const item = res.r.item, size = res.r.size ?? item.meta?.size ?? 0, avatar = game.you()?.avatar;
          game.sound.sfx('catch');
          if (avatar) {
            const tool = s.tool;
            // The rod back in hand after showing the fish off, unless the fish itself landed in your hand (pockets full): the catch
            // card is still asking Drop / Let it go about it.
            avatar.play('holdUp', () => { if (item.at.loc !== 'hand') void game.cmd.holdTool(tool); });
            avatar.hold(buildFish((getItem(item.catalogId) as FishEntry).look, size || undefined, item.catalogId), 'both');
          }
          s.fishingSetCatch({ catalogId: item.catalogId, size, item });
          s.openScreen('catch', undefined);
        } else {
          fishingLayerOf(game)?.splash({ x: shadow.x, y: shadow.y });
          game.sound.sfx('splash');
          s.toast(res.r.result === 'early' ? 'fishing.tooEarly' : 'fishing.missed');
        }
      },
    });
    loops.set(game, loop);
    setTreeClock(() => game.now());                                    // saplings and fruit follow the synced town clock
    // Debounced per town hour: `fish.shadows` counts against the 10/s action bucket, and a reconnect (fresh snapshot + clock
    // resync) can emit `hour:changed` for an hour we already asked about or whose shadows the snapshot already carries.
    let asked: string | null = null;
    game.bus.on('hour:changed', (e) => {
      if (e.hourKey !== asked && useGame.getState().you?.yields.fish.hourKey !== e.hourKey) {
        asked = e.hourKey;
        void game.cmd.act('fish.shadows', {}).then((res) => {
          if (!res.ok) { asked = null; return; }                           // let the next hour:changed retry
          if (useGame.getState().you) useGame.getState().applyPatch({ yields: { fish: res.r } });
          fishingLayerOf(game)?.setShadows(res.r.shadows);
        });
      }
      const you = useGame.getState().you;
      if (you) game.scenes.out?.refreshPerResident(you);                 // saplings grow, fruit returns on the hour
    });
    // B cancels: the keyboard's Escape arrives as an Input 'B' press, the HUD's B as "held" (it runs while held). Walking away or
    // leaving the outdoors cancels too.
    let boundInput: Input | null = null, unbindB: (() => void) | null = null;
    return {
      name: 'interaction',
      update(f) {
        if (game.input && game.input !== boundInput) {
          unbindB?.();
          boundInput = game.input; unbindB = boundInput.on('B', () => { void loop.cancel(); });
        }
        if (loop.phase === 'casting' || loop.phase === 'waiting' || loop.phase === 'bite') {
          const you = game.you(), stick = game.input?.vector();
          const away = !you || you.space !== 'out' || (castFrom !== null && Math.hypot(you.pos.x - castFrom.x, you.pos.y - castFrom.y) > WALK_AWAY);
          if (away || (stick && (stick.running || Math.hypot(stick.x, stick.y) > STICK_DEADZONE))) void loop.cancel();
        }
        loop.tick();
        fishingLayerOf(game)?.update(f.dt);
        tickTreeShakes((id) => {
          const o = objectsOf(game).find((x) => x.id === id);          // the same outdoor index the interactions use
          if (o) game.scenes.out?.updateObject(o);
        });
      },
    };
  },
};
